/**
 * 철광석 수소환원(유동/샤프트로) + 전기용융로(ESF) 탄소환원 off-gas.
 *
 * ⚠ 표준식이 아니다 — 일반적인 DRI(직접환원철) 공정 단순화다:
 *   철산화물은 3단계로 환원된다 (Fe2O3 → Fe3O4 → FeO → Fe). 1·2단계는
 *   반응성이 좋아 사실상 항상 완결된다고 보고, **율속·최종 단계인
 *   3단계(FeO→Fe)만 목표 금속화율만큼 부분 진행**된다고 본다 — DRI
 *   공정에서 흔히 쓰는 단순화다. 이 가정이 안 맞는 공정(예: 저온에서
 *   1·2단계도 불완전한 경우)에는 쓸 수 없다.
 *
 *   O/Fe 몰비: Fe2O3 = 1.5, FeO = 1.0, Fe = 0.
 *   → 1·2단계가 떼어내는 O/Fe = 1.5 − 1.0 = 0.5 (항상 고정)
 *   → 3단계가 목표금속화율 M(0~1) 만큼 떼어내는 O/Fe = M
 *   → mol Fe 당 소모 H2 = 0.5 + M   (H2 1몰이 O 1개를 떼어 H2O 1몰이 됨)
 *   경계 확인: M=0 → 0.5, M=1(완전 금속화) → 1.5 (Fe2O3 의 O/Fe 전체와 일치)
 *
 *   ESF 쪽은 유동로와 이어지지 않는 별도 노드다 — 광석 유량·(도달)
 *   금속화율을 다시 입력받아 잔여 미환원 FeO 양을 그 자리에서 계산하고,
 *   FeO + C → Fe + CO (1:1:1:1 몰비, 탄소환원)로 CO 발생량을 구한다.
 */

/** Fe2O3 분자량 (kg/kmol) — 2×55.845(Fe) + 3×15.999(O) */
export const MW_FE2O3 = 159.687;
/** FeO 분자량 (kg/kmol) — 55.845(Fe) + 15.999(O) */
export const MW_FEO = 71.844;
/** H2 분자량 (kg/kmol) */
export const MW_H2 = 2.016;
/** H2O 분자량 (kg/kmol) */
export const MW_H2O = 18.015;
/** 탄소(C) 원자량 (kg/kmol) */
export const MW_C = 12.011;
/** CO 분자량 (kg/kmol) */
export const MW_CO = 28.010;

/** 입구 가스에서 수소를 찾을 때 쓰는 `gasComponents.ts` 의 성분 key. */
export const H2_COMPONENT_KEY = "hydrogen";

/* ── 유동/샤프트로 (H2 환원) ─────────────────────────────── */

export interface ReductionInput {
  /** 광석 중 Fe2O3 질량유량 (kg/h). */
  readonly oreFe2O3MassFlowKgH: number;
  /** 목표 금속화율 (금속 Fe / 전체 Fe × 100, %). */
  readonly targetMetallizationPercent: number;
  /** 입구 가스의 건가스 성분별 질량유량 (kg/h) — H2 를 포함해야 반응이 된다. */
  readonly inletGasComponents: Readonly<Record<string, number>>;
}

export interface ReductionSuccess {
  readonly ok: true;
  /** 반응 후 건가스 성분(H2 가 소모된 만큼 줄어든 값, 나머지는 그대로). */
  readonly outletGasComponents: Readonly<Record<string, number>>;
  /** 목표 금속화율을 채우는 데 필요한 H2 (kg/h). */
  readonly requiredH2MassFlowKgH: number;
  /** 실제로 소모된 H2 (kg/h) — 공급이 부족하면 requiredH2 보다 작다. */
  readonly consumedH2MassFlowKgH: number;
  /** 생성된 수증기(H2O) 질량유량 (kg/h). */
  readonly producedH2OMassFlowKgH: number;
  /** 공급 H2 로 실제 도달 가능한 금속화율 (%). 공급이 충분하면 목표와 같다. */
  readonly achievedMetallizationPercent: number;
  /** 공급 H2 가 부족해 목표 금속화율에 못 미칠 때만 채워진다. */
  readonly warning?: string;
}

export interface ReductionFailure {
  readonly ok: false;
  readonly reason: "invalid-input";
  readonly message: string;
}

export type ReductionResult = ReductionSuccess | ReductionFailure;

export function reduceIronOreWithHydrogen(input: ReductionInput): ReductionResult {
  const { oreFe2O3MassFlowKgH, targetMetallizationPercent } = input;
  if (!Number.isFinite(oreFe2O3MassFlowKgH) || oreFe2O3MassFlowKgH <= 0) {
    return { ok: false, reason: "invalid-input", message: "광석 Fe2O3 유량은 0보다 커야 합니다." };
  }
  if (
    !Number.isFinite(targetMetallizationPercent) ||
    targetMetallizationPercent < 0 ||
    targetMetallizationPercent > 100
  ) {
    return { ok: false, reason: "invalid-input", message: "목표 금속화율은 0~100% 사이여야 합니다." };
  }

  const M = targetMetallizationPercent / 100;
  const molFe = (2 * oreFe2O3MassFlowKgH) / MW_FE2O3;
  const requiredH2Mol = molFe * (0.5 + M);
  const requiredH2MassFlowKgH = requiredH2Mol * MW_H2;

  const availableH2MassFlowKgH = input.inletGasComponents[H2_COMPONENT_KEY] ?? 0;
  const consumedH2MassFlowKgH = Math.min(requiredH2MassFlowKgH, availableH2MassFlowKgH);
  const consumedH2Mol = consumedH2MassFlowKgH / MW_H2;
  const producedH2OMassFlowKgH = consumedH2Mol * MW_H2O;

  const achievedMetallizationPercent =
    molFe > 0 ? Math.max(0, Math.min(1, consumedH2Mol / molFe - 0.5)) * 100 : 0;

  const outletGasComponents: Record<string, number> = { ...input.inletGasComponents };
  outletGasComponents[H2_COMPONENT_KEY] = Math.max(
    0,
    availableH2MassFlowKgH - consumedH2MassFlowKgH,
  );

  const warning =
    consumedH2MassFlowKgH < requiredH2MassFlowKgH - 1e-9
      ? `공급 H2(${availableH2MassFlowKgH.toFixed(1)} kg/h)로는 목표 금속화율 ` +
        `${targetMetallizationPercent.toFixed(1)}% 를 채울 수 없습니다 — 이 유량으로 도달 ` +
        `가능한 금속화율은 약 ${achievedMetallizationPercent.toFixed(1)}% 입니다.`
      : undefined;

  return {
    ok: true,
    outletGasComponents,
    requiredH2MassFlowKgH,
    consumedH2MassFlowKgH,
    producedH2OMassFlowKgH,
    achievedMetallizationPercent,
    ...(warning !== undefined ? { warning } : {}),
  };
}

/* ── 전기용융로(ESF) 탄소환원 off-gas ───────────────────── */

export interface EsfOffgasInput {
  /** 광석 중 Fe2O3 질량유량 (kg/h) — 유동로와 별도로 다시 입력받는다. */
  readonly oreFe2O3MassFlowKgH: number;
  /** 이 광석이 ESF 에 들어오기 전 도달한 금속화율 (%). */
  readonly metallizationPercent: number;
  /** 공급 탄소량 (kg/h). */
  readonly carbonMassFlowKgH: number;
}

export interface EsfOffgasSuccess {
  readonly ok: true;
  /** 미환원 FeO 질량유량 (kg/h) — 탄소 공급과 무관하게, 금속화율에서 정해진다. */
  readonly unreactedFeOMassFlowKgH: number;
  /** 이 FeO 를 전부 반응시키는 데 필요한 탄소량 (kg/h). */
  readonly requiredCarbonMassFlowKgH: number;
  /** 실제로 반응한 탄소량 (kg/h). */
  readonly consumedCarbonMassFlowKgH: number;
  /** 발생하는 CO 질량유량 (kg/h). */
  readonly coMassFlowKgH: number;
  /** 탄소 과부족이 있을 때만 채워진다. */
  readonly warning?: string;
}

export interface EsfOffgasFailure {
  readonly ok: false;
  readonly reason: "invalid-input";
  readonly message: string;
}

export type EsfOffgasResult = EsfOffgasSuccess | EsfOffgasFailure;

export function esfOffgasFromCarbon(input: EsfOffgasInput): EsfOffgasResult {
  const { oreFe2O3MassFlowKgH, metallizationPercent, carbonMassFlowKgH } = input;
  if (!Number.isFinite(oreFe2O3MassFlowKgH) || oreFe2O3MassFlowKgH <= 0) {
    return { ok: false, reason: "invalid-input", message: "광석 Fe2O3 유량은 0보다 커야 합니다." };
  }
  if (
    !Number.isFinite(metallizationPercent) ||
    metallizationPercent < 0 ||
    metallizationPercent > 100
  ) {
    return { ok: false, reason: "invalid-input", message: "금속화율은 0~100% 사이여야 합니다." };
  }
  if (!Number.isFinite(carbonMassFlowKgH) || carbonMassFlowKgH < 0) {
    return { ok: false, reason: "invalid-input", message: "탄소 공급량은 0 이상이어야 합니다." };
  }

  const M = metallizationPercent / 100;
  const molFe = (2 * oreFe2O3MassFlowKgH) / MW_FE2O3;
  // 3단계(FeO→Fe)에서 (1−M) 만큼 미환원 상태로 남는다.
  const molFeO = molFe * (1 - M);
  const unreactedFeOMassFlowKgH = molFeO * MW_FEO;
  const requiredCarbonMassFlowKgH = molFeO * MW_C; // FeO + C → Fe + CO, 1:1
  const consumedCarbonMassFlowKgH = Math.min(carbonMassFlowKgH, requiredCarbonMassFlowKgH);
  const molCO = consumedCarbonMassFlowKgH / MW_C;
  const coMassFlowKgH = molCO * MW_CO;

  let warning: string | undefined;
  if (carbonMassFlowKgH < requiredCarbonMassFlowKgH - 1e-9) {
    warning =
      `공급 탄소(${carbonMassFlowKgH.toFixed(1)} kg/h)로는 잔여 FeO(${unreactedFeOMassFlowKgH.toFixed(1)} kg/h)를 ` +
      "다 반응시킬 수 없습니다 — 그만큼 FeO 가 녹지 않고 남습니다.";
  } else if (carbonMassFlowKgH > requiredCarbonMassFlowKgH + 1e-9) {
    warning =
      `공급 탄소(${carbonMassFlowKgH.toFixed(1)} kg/h)가 반응에 필요한 양` +
      `(${requiredCarbonMassFlowKgH.toFixed(1)} kg/h)보다 많습니다 — 과잉분` +
      `(${(carbonMassFlowKgH - requiredCarbonMassFlowKgH).toFixed(1)} kg/h)은 반응하지 않은 것으로 ` +
      "보고 이 노드는 추적하지 않습니다.";
  }

  return {
    ok: true,
    unreactedFeOMassFlowKgH,
    requiredCarbonMassFlowKgH,
    consumedCarbonMassFlowKgH,
    coMassFlowKgH,
    ...(warning !== undefined ? { warning } : {}),
  };
}
